import React, { type PropsWithChildren, useEffect, useMemo } from 'react';
import Prism from 'prismjs';
import beautify from 'js-beautify';
import Button from '@rio-cloud/rio-uikit/Button';
import Notification from '@rio-cloud/rio-uikit/Notification';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import type { SimpleTooltipProps } from '@rio-cloud/rio-uikit/SimpleTooltip';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';

// Require all needed languages
import 'prismjs/components/prism-typescript.min.js';
import 'prismjs/components/prism-jsx.min.js';
import 'prismjs/components/prism-tsx.min.js';
import 'prismjs/components/prism-css.min.js';
import 'prismjs/components/prism-css-extras.min.js';
import 'prismjs/components/prism-bash.js';
import 'prismjs/components/prism-less.js';
import { mergeClassNameOverrides } from '@rio-cloud/rio-uikit/mergeClassNameOverrides';

// Require all needed plugins
// import 'prismjs/plugins/line-numbers/prism-line-numbers.js';
// import 'prismjs/plugins/line-numbers/prism-line-numbers.css';

type CopyTooltipStyle = NonNullable<SimpleTooltipProps['style']> & {
    '--anchor-offset-block': string;
};

const copyTooltipStyle: CopyTooltipStyle = { '--anchor-offset-block': '5px' };

export const withoutReactComment = (text: string) => text.replace(/<!--[\s\S]*?-->/gm, '');

type CodeBlockProps = {
    language?: string;
    overflow?: boolean | 'auto' | 'scroll';
    className?: string;
    wrapperClassName?: string;
    exampleRef?: React.RefObject<HTMLDivElement>;
    exampleElement?: HTMLElement | null;
};

export const CodeBlock = React.memo((props: PropsWithChildren<CodeBlockProps>) => {
    const {
        language,
        overflow = false,
        className,
        wrapperClassName = '',
        children,
        exampleRef,
        exampleElement,
    } = props;

    const clipboard = useClipboard();
    const markupElement = exampleElement ?? exampleRef?.current;

    const extractFormattedMarkup = (element: HTMLElement) => {
        const withoutReactComments = withoutReactComment(element.innerHTML);
        const lineBreaks = withoutReactComments.split('><').join('>\n<');
        const formattedMarkup = beautify.html(lineBreaks, { indent_size: 4 });
        return formattedMarkup;
    };

    const code = markupElement ? extractFormattedMarkup(markupElement) : children;

    const codeText = String(code);
    const prismLanguage = language
        ?.split(/\s+/)
        .find(className => className.startsWith('language-'))
        ?.slice('language-'.length);
    const prismLanguageClassName = prismLanguage ? `language-${prismLanguage}` : '';

    const highlightedCode = useMemo(() => {
        const grammar = prismLanguage ? Prism.languages[prismLanguage] : undefined;

        return grammar ? Prism.highlight(codeText, grammar, prismLanguage) : Prism.util.encode(codeText);
    }, [codeText, prismLanguage]);

    useEffect(() => {
        clipboard.copied && Notification.default('Code copied to clipboard');
    }, [clipboard.copied]);

    const handleCopy = () => clipboard.copy(String(code));

    const wrapClassName = 'uikit-code-block-wrap';
    const overflowClassName =
        overflow === false ? wrapClassName : overflow === 'scroll' ? 'overflow-x-scroll' : 'overflow-auto';
    const codeClassName = overflow === false ? `${language ?? ''} ${wrapClassName}`.trim() : language;

    return (
        <div className={mergeClassNameOverrides('position-relative margin-bottom-10', className)}>
            <div className='position-absolute top-5 right-5'>
                <SimpleTooltip content='Copy to clipboard' placement='top' style={copyTooltipStyle}>
                    <Button
                        aria-label='Copy code to clipboard'
                        bsStyle='muted'
                        bsSize='sm'
                        iconName='duplicate'
                        iconOnly
                        onClick={handleCopy}
                    />
                </SimpleTooltip>
            </div>
            <pre
                className={`padding-10 padding-right-50 margin-bottom-0 ${overflowClassName} ${prismLanguageClassName} ${wrapperClassName}`}
            >
                <code className={codeClassName} dangerouslySetInnerHTML={{ __html: highlightedCode }} />
            </pre>
        </div>
    );
});
